import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_URL } from "../../consts";

// Components
import CardItem from "../common/CardItem";
import HomeCardMedia from "./HomeCardMedia";
import HomeCardHeader from "./HomeCardHeader";
import HomeCardBody from "./HomeCardBody";
import AddToSpace from "./AddToSpace";
import HomeCardDialog from "./HomeCardDialog";
import SummaryEditorDialog from "../Ai/SummaryEditorDialog";
import RatingObject from "../RatingObject/RatingObject";
import CommentsDialog from "./CommentsDialog";

const HomeCardItem = ({
  isFeedback,
  isBookmark,
  displayRemoveFromSpace,
  displayAddToSpace,
  data,
  searhTags,
  applyMargin,
  onRateSuccessfully,
  displayStars,
  onRemoveFromSpaceClick,
  onAddToSpaceClick,
  showRemove,
  onRemove,
  onDelete, // Add delete handler prop
  selected,
  isSelected,
  onSelect,
  showSpacePopup, // Add this prop
  enableDragAndDrop, // New prop to enable drag and drop
  currentSpaceId, // Add current space context
  parentSpaceId, // Add parent space ID
  hideUserInfo = false, // Hide user information in card
  isCommunity = false, // Add isCommunity prop
  onVisibilityUpdate, // Callback when visibility is updated
}) => {
  const dispatch = useDispatch();
  const authorized = useSelector((state) => state.authorized);

  // State
  const [readingDialogOpen, setReadingDialog] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [openSpaceModal, setOpenSpaceModal] = useState(false);
  const [showRatingObject, setShowRatingObject] = useState(false);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [rateId, setRateId] = useState(null);
  const [openedItemData, setDetailedDataDialog] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localData, setLocalData] = useState(data);
  const isSpacePage = window.location.pathname.includes('/spaces/');

  // Sync localData with data prop when it changes
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  // Handlers
  const handleSpaceModalClose = () => {
    setOpenSpaceModal(false);
  };

  const handleRatingObjectClose = () => {
    setShowRatingObject(false);
  };

  const handleSave = async (receivedHtml, objectId) => {
    setReadingDialog(false);
    try {
      const { data: responseData } = await axios.post(`${API_URL}/documents`, {
        content: receivedHtml,
        object_id: objectId,
      });
      return responseData;
    } catch (error) {
      console.error("Save error:", error);
      throw error;
    }
  };

  const handleCardClick = (e, cardData) => {
    // Don't open dialog if clicking on menu button or its children
    if (e.target.closest('#card-menu-button') || e.target.closest('[aria-controls="card-menu"]')) {
      return;
    }

    // Don't open dialog if clicking on profile image or name
    if (e.target.closest('[data-profile-click]') || 
        e.target.closest('.MuiAvatar-root') ||
        (e.target.tagName === 'TYPography' && e.target.closest('[data-profile-click]'))) {
      return;
    }

    if (!openSpaceModal && !isDragging) {
      setDetailedDataDialog(cardData);
    }
  };

  // Handle data update from dialog (e.g., when image is uploaded)
  const handleDataUpdate = (updatedData) => {
    // Update local data state to reflect changes
    setLocalData(updatedData);
    // Also update the opened dialog data if dialog is open
    if (openedItemData) {
      setDetailedDataDialog(updatedData);
    }
  };

  const handleMenuAction = (action, data) => {
    if (action === 'spaceClick') {
      axios.get(`${API_URL}/rates/ratings/${localData.rating_id}`)
        .then(({ data: responseData }) => {
          setDetailedDataDialog(responseData);
        })
        .catch((error) => {
          console.error("Error fetching space data:", error);
          dispatch({
            type: "SET_POPUP",
            payload: {
              text: "Error loading space data",
              type: "error",
            },
          });
        });
    }
  };

  const handleAddToSpaceClick = () => {
    // If we're in a space page, use object_id instead of id
    setRateId(isSpacePage ? localData.object_id : localData.id);
    setShowRatingObject(true);
  };

  const handleOpenComments = (e) => {
    e.stopPropagation();
    setShowCommentsDialog(true);
  };

  // Drag and Drop handlers
  const handleDragStart = (e) => {
    if (!enableDragAndDrop) return;

    setIsDragging(true);
    e.dataTransfer.effectAllowed = "copy";

    // Store the rated object data for the drop handler
    const dragData = {
      type: "rated-object",
      ratedObject: {
        id: localData.id, // This is the rating ID
        title: localData.title,
        comment: localData.comment,
        rate_points: localData.rate_points,
        tags: localData.tags || [],
        image: localData.image || localData.image_url || "",
        rated_object_id: localData.rated_object_id || localData.id, // This is the actual object ID
        object_id: localData.object_id || localData.rated_object_id || localData.id, // Ensure we have object_id
        linkUrl: localData.metadata?.page_url || "",
        // Include all data needed for AddToSpace dialog
        fullData: localData
      }
    };

    e.dataTransfer.setData("application/json", JSON.stringify(dragData));

    // Create a custom drag image
    const dragGhost = document.createElement("div");
    dragGhost.textContent = `📄 ${localData.title || 'Rated Object'}`;
    dragGhost.style.position = "absolute";
    dragGhost.style.top = "-9999px";
    dragGhost.style.left = "-9999px";
    dragGhost.style.fontSize = "14px";
    dragGhost.style.fontWeight = "bold";
    dragGhost.style.padding = "8px 12px";
    dragGhost.style.borderRadius = "6px";
    dragGhost.style.backgroundColor = "#fff";
    dragGhost.style.border = "2px solid #2196F3";
    dragGhost.style.boxShadow = "0px 4px 12px rgba(33, 150, 243, 0.3)";
    dragGhost.style.color = "#2196F3";
    dragGhost.style.maxWidth = "300px";
    dragGhost.style.whiteSpace = "nowrap";
    dragGhost.style.overflow = "hidden";
    dragGhost.style.textOverflow = "ellipsis";

    document.body.appendChild(dragGhost);
    e.dataTransfer.setDragImage(dragGhost, 0, 0);

    setTimeout(() => {
      document.body.removeChild(dragGhost);
    }, 0);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <>
      <div
        draggable={enableDragAndDrop}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        style={{
          cursor: enableDragAndDrop ? 'grab' : 'default',
          opacity: isDragging ? 0.7 : 1,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          width: '100%',
          height: '100%',
          transform: isDragging ? 'scale(1.02)' : 'scale(1)',
          filter: isDragging ? 'blur(1px)' : 'none',
        }}
      >
        <CardItem
          data={localData}
          searhTags={searhTags}
          selected={selected}
          isSelected={isSelected}
          displayStars={displayStars}
          isBookmark={isBookmark}
          isFeedback={isFeedback}
          applyMargin={applyMargin}
          onRemoveFromSpaceClick={onRemoveFromSpaceClick}
          onAddToSpaceClick={handleAddToSpaceClick}
          onRemove={onRemove}
          onDelete={onDelete}
          onRateSuccessfully={onRateSuccessfully}
          onSelect={onSelect}
          displayRemoveFromSpace={displayRemoveFromSpace}
          displayAddToSpace={displayAddToSpace}
          showRemove={showRemove}
          showSpacePopup={showSpacePopup}
          onOpenComments={handleOpenComments}
          hideUserInfo={hideUserInfo}
          isCommunity={isCommunity}
          onVisibilityUpdate={onVisibilityUpdate}
          CustomMedia={HomeCardMedia}
          CustomHeader={HomeCardHeader}
          CustomBody={HomeCardBody}
          onCardClick={handleCardClick}
          onMenuAction={handleMenuAction}
        />
      </div>

      {/* Add to Space Dialog */}
      {openSpaceModal && (
        <AddToSpace
          open={openSpaceModal}
          rateId={rateId}
          objectId={isSpacePage ? localData.object_id : localData.id}
          itemData={localData}
          currentSpaceId={currentSpaceId}
          handleClose={handleSpaceModalClose}
          authorized={authorized}
        />
      )}

      {/* Home Card Dialog */}
      {openedItemData && (
        <HomeCardDialog
          open={openedItemData}
          data={openedItemData}
          onReadMore={() => setReadingDialog(true)}
          onUpdate={handleDataUpdate}
          onClose={(e) => {
            e.stopPropagation();
            setDetailedDataDialog(null);
          }}
        />
      )}

      {/* Summary Editor Dialog */}
      <SummaryEditorDialog
        open={readingDialogOpen}
        onSave={(content) => handleSave(content, localData.id)}
        onClose={() => setReadingDialog(false)}
        objectId={localData.id}
      />

      {/* Rating Object Dialog for Add to Space */}
      <RatingObject
        open={showRatingObject}
        handleClose={handleRatingObjectClose}
          id={rateId}
          initialData={localData}
        preselectedSpaceId={currentSpaceId}
        preselectedParentSpaceId={parentSpaceId}
        onRateSuccessfully={(id) => {
          setShowRatingObject(false);
          if (onRateSuccessfully) {
            onRateSuccessfully(id);
          }
        }}
      />

      {/* Comments Dialog */}
      <CommentsDialog
        open={showCommentsDialog}
        onClose={() => setShowCommentsDialog(false)}
        rateId={localData.id}
      />
    </>
  );
};

HomeCardItem.defaultProps = {
  displayStars: true,
  displayStats: true,
  displayAddToSpace: false,
};

export default HomeCardItem;
